import React, { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Loader2, CheckCircle, XCircle, Mail } from 'lucide-react';

const EmailVerificationPage: React.FC = () => {
  const [message, setMessage] = useState('Verifying your email...');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const { data: authListener } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_IN' && session?.user) {
        setMessage('Email verified successfully! You will be redirected to login.');
        setIsLoading(false);
        setTimeout(() => navigate('/login'), 3000);
        authListener?.subscription.unsubscribe();
      } else if (session?.user?.email_confirmed_at) {
        setMessage('Email already verified. Redirecting...');
        setIsLoading(false);
        setTimeout(() => navigate('/'), 3000);
        authListener?.subscription.unsubscribe();
      }
    });

    const checkCurrentUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user?.email_confirmed_at) {
        setMessage('Email successfully verified or already verified. Redirecting...');
        setIsLoading(false);
        setTimeout(() => navigate(user ? '/' : '/login'), 3000);
        authListener?.subscription.unsubscribe();
      } else if (location.search.includes('error_description')) {
        const params = new URLSearchParams(location.search);
        setError(params.get('error_description'));
        setMessage('');
        setIsLoading(false);
        authListener?.subscription.unsubscribe();
      } else {
        setTimeout(() => {
          if (!error && message === 'Verifying your email...') {
            setMessage('Verification link processed. If successful, you can now log in. If issues persist, please try the link again or contact support.');
            setIsLoading(false);
          }
        }, 5000);
      }
    };

    checkCurrentUser();

    return () => {
      authListener?.subscription.unsubscribe();
    };
  }, [navigate, location.search, error, message]);

  return (
    <div 
      className='flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4 transition-colors duration-500' 
      style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '100vh',
        width: '100%',
        position: 'fixed',
        top: 0,
        left: 0
      }}
    >
      <div className='w-full max-w-md'>
        <Card className='shadow-xl border-0 bg-white/80 backdrop-blur-sm transition-all duration-300 hover:shadow-2xl'>
          <CardHeader className='text-center pb-8'>
            <CardTitle className='text-3xl font-bold text-slate-900'>
              Email Verification
            </CardTitle>
            <CardDescription className='text-slate-600 mt-2'>
              {isLoading ? 'Processing your verification...' : 'Verification complete'}
            </CardDescription>
          </CardHeader>
          <CardContent className='pb-8'>
            <div className='space-y-6'>
              <div className='flex items-center justify-center p-8'>
                {isLoading ? (
                  <Loader2 className='h-12 w-12 animate-spin text-primary' />
                ) : error ? (
                  <XCircle className='h-12 w-12 text-red-500' />
                ) : (
                  <CheckCircle className='h-12 w-12 text-green-500' />
                )}
              </div>
              
              <div className='text-center'>
                {error ? (
                  <div className='p-4 bg-red-50 border border-red-200 rounded-lg'>
                    <div className='flex items-start'>
                      <XCircle className='h-5 w-5 text-red-600 mr-2 mt-0.5 flex-shrink-0' />
                      <div>
                        <p className='text-sm font-medium text-red-800'>Verification failed</p>
                        <p className='text-sm text-red-700 mt-1'>{error}</p>
                      </div>
                    </div>
                  </div>
                ) : message ? (
                  <div className='p-4 bg-green-50 border border-green-200 rounded-lg'>
                    <div className='flex items-start'>
                      <CheckCircle className='h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0' />
                      <div>
                        <p className='text-sm font-medium text-green-800'>Success!</p>
                        <p className='text-sm text-green-700 mt-1'>{message}</p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className='p-4 bg-blue-50 border border-blue-200 rounded-lg'>
                    <div className='flex items-start'>
                      <Mail className='h-5 w-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0' />
                      <div>
                        <p className='text-sm font-medium text-blue-800'>Processing...</p>
                        <p className='text-sm text-blue-700 mt-1'>Please wait while we verify your email</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            <div className='mt-8 space-y-4 text-center'>
              <div className='relative'>
                <div className='absolute inset-0 flex items-center'>
                  <div className='w-full border-t border-slate-200' />
                </div>
                <div className='relative flex justify-center text-sm'>
                  <span className='px-4 bg-white text-slate-500'>Continue to app</span>
                </div>
              </div>
              <p className='text-sm text-slate-600'>
                <Link 
                  to='/login' 
                  className='font-medium text-primary hover:text-primary/80 transition-colors duration-200'
                >
                  Go to login
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default EmailVerificationPage;